import "flatpickr/dist/flatpickr.css";
import "simplebar-react/dist/simplebar.min.css";
import "../scss/tailwind.scss";
import "../scss/icons.scss";
import "../scss/fonts/fonts.scss";
import "../scss/plugins.scss";

import { InertiaProgress } from "@inertiajs/progress";
import { createRoot } from "react-dom/client";
import { createInertiaApp } from "@inertiajs/react";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import { Provider } from "react-redux";
import store, { AppDispatch } from "../js/slices/reducer";
import { useEffect } from "react";
import { getPreviousThemeData } from "./slices/layout/utils";
import {
    changeDarkModeClass,
    changeDataColor,
    changeDirection,
    changeLayout,
    changeLayoutContentWidth,
    changeLayoutLanguage,
    changeLayoutMode,
    changeModernNavigation,
    changeSidebarColor,
    changeSidebarSize,
} from "./slices/thunk";
import { LAYOUT_LANGUAGES } from "./components/Constants/layout";
import { initialState } from "./slices/layout/reducer";

const appName = "Bajaj - CV Audit Admin";

// Service Worker registration
const registerServiceWorker = () => {
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
        window.addEventListener('load', () => {
            navigator.serviceWorker.register('/sw.js')
                .then(registration => {
                    console.log('ServiceWorker registration successful with scope:', registration.scope);
                })
                .catch(error => {
                    console.error('ServiceWorker registration failed:', error);
                });

            // Listen for service worker updates
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                console.log('Service worker controller changed - reloading for fresh assets');
                window.location.reload();
            });
        });
    }
};

// Initialize Inertia Progress
InertiaProgress.init({
    color: '#4B5563',
    showSpinner: true,
    delay: 250,
});

// Add prefetching for faster navigation
document.addEventListener('inertia:navigate', (event) => {
    // Create preconnect link for faster initial connection
    const preconnectLink = document.createElement('link');
    preconnectLink.rel = 'preconnect';
    preconnectLink.href = window.location.origin;
    document.head.appendChild(preconnectLink);
});

const ThemeInitializer = () => {
    useEffect(() => {
        // Register service worker on component mount
        registerServiceWorker();

        const dispatch = store.dispatch as AppDispatch;
        dispatch(
            changeLayoutMode(
                getPreviousThemeData("dx-layout-mode") ??
                    initialState.layoutMode
            )
        );
        dispatch(
            changeLayoutContentWidth(
                getPreviousThemeData("dx-layout-content-width") ??
                    initialState.layoutWidth
            )
        );
        dispatch(
            changeSidebarSize(
                getPreviousThemeData("dx-sidebar-size") ??
                    initialState.layoutSidebar
            )
        );
        dispatch(
            changeDirection(
                getPreviousThemeData("dx-layout-direction") ??
                    initialState.layoutDirection
            )
        );
        dispatch(
            changeLayout(
                getPreviousThemeData("dx-layout-type") ??
                    initialState.layoutType
            )
        );
        dispatch(
            changeSidebarColor(
                getPreviousThemeData("dx-sidebar-colors") ??
                    initialState.layoutSidebarColor
            )
        );
        dispatch(
            changeLayoutLanguage(
                getPreviousThemeData("dx-layout-language") ??
                    LAYOUT_LANGUAGES.ENGLISH
            )
        );
        dispatch(
            changeDataColor(
                getPreviousThemeData("dx-theme-color") ??
                    initialState.layoutDataColor
            )
        );
        dispatch(
            changeDarkModeClass(
                getPreviousThemeData("dx-theme-dark-class") ??
                    initialState.layoutDarkModeClass
            )
        );
        dispatch(
            changeModernNavigation(
                getPreviousThemeData("dx-theme-nav-type") ??
                    initialState.layoutNavigation
            )
        );
        const htmlElement = document.documentElement;
        htmlElement.classList.add("scroll-smooth", "group");
        return () => {
            htmlElement.classList.remove("scroll-smooth", "group");
        };
    }, []);

    return null; // This component doesn't render anything
};

// Enhanced asset loading with Inertia setup
createInertiaApp({
    title: (title) => `${title} | ${appName}`,
    resolve: (name) =>
        resolvePageComponent(
            `./Pages/${name}.tsx`,
            import.meta.glob("./Pages/**/*.tsx")
        ),
    setup({ el, App, props }) {
        const root = createRoot(el);

        root.render(
            <Provider store={store}>
                <ThemeInitializer />
                <App {...props} />
            </Provider>
        );
    },
});